import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import './ProductCard.scss';
import { Crossplatform, DataSearching } from "../../../../../../../assets/svg";

type ProductItem = {
  id: number;
  productList: string;
  icon: React.ReactNode;
};

const DEFAULT_SHOW_FIVE = true;
const SHOW_FIVE_URL = "/ui_config/showFIVE.txt";

const ICON_DEFS: Array<{
  id: number;
  productList: string;
  icon: React.ReactNode;
  show: (showFive: boolean) => boolean;
}> = [
  { id: 1, productList: "Platform",  icon: <Crossplatform />, show: () => true },
  { id: 2, productList: "FiVe",      icon: <DataSearching />, show: (showFive) => showFive },
];

function toProducts(showFive: boolean): ProductItem[] {
  return ICON_DEFS.filter((d) => d.show(showFive)).map((d) => ({
    id: d.id, productList: d.productList, icon: d.icon,
  }));
}

function parseShowFiveFlag(raw: string): boolean {
  const normalized = String(raw ?? "")
    .replace(/^\uFEFF/, "")
    .trim();
  if (normalized === "0") return false;
  if (normalized === "1") return true;
  return DEFAULT_SHOW_FIVE;
}

interface IProductProps {
  onClick?: (pDirectory: any) => void;
}

const handleKeyActivate = (cb: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); cb(); }
};

function ProductCard({ onClick }: IProductProps) {
  const [products, setProducts] = useState<ProductItem[]>(() => toProducts(DEFAULT_SHOW_FIVE));

  useEffect(() => {
    let active = true;
    axios
      .get(SHOW_FIVE_URL, {
        responseType: "text",
        params: { _: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        validateStatus: (status) => status === 200 || status === 404,
      })
      .then((res: { status: number; data: string }) => {
        if (!active) return;
        if (res.status !== 200) {
          setProducts(toProducts(DEFAULT_SHOW_FIVE));
          return;
        }
        setProducts(toProducts(parseShowFiveFlag(res.data)));
      })
      .catch(() => {
        if (!active) return;
        setProducts(toProducts(DEFAULT_SHOW_FIVE));
      });

    return () => { active = false; };
  }, []);

  const middleClass = useMemo(
    () => (products.length === 1 ? "middle single" : "middle"),
    [products.length]
  );

  return (
    <div className="product-card">
      <div className="product">Products</div>
      <div className={middleClass}>
        {products.map((item) => (
          <div
            className="wrapper"
            key={item.id}
            role="button"
            tabIndex={0}
            aria-label={`Open ${item.productList}`}
            onClick={() => onClick && onClick(item.productList)}
            onKeyDown={handleKeyActivate(() => onClick && onClick(item.productList))}
          >
            <div className="icon-wrapper" aria-hidden="true">{item.icon}</div>
            <p className="product-title">{item.productList}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProductCard;
